package com.evoapps.evoshift.widgets

import com.evoapps.evoshift.ui.theme.motionTween
import androidx.compose.animation.Crossfade
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.photo.AvatarImages
import org.kodein.di.compose.localDI
import org.kodein.di.direct
import org.kodein.di.instanceOrNull

/** "Jan Kowalski" → "JK"; "?" without a name. */
fun initialsOf(name: String): String =
    name.split(" ").filter { it.isNotBlank() }.take(2).joinToString("") { it.first().uppercase() }.ifEmpty { "?" }

/**
 * A person's round avatar (SH-94): their profile photo ([photoUrl]: an uploaded one or their
 * Google photo), or their initials while it loads, without one, or when it cannot be loaded.
 * [preview] (a photo just cropped, still uploading) shows instead of [photoUrl].
 * Decorative: the name is always shown next to it, so it has no content description.
 */
@Composable
fun UserAvatar(
    name: String,
    photoUrl: String?,
    size: Dp,
    modifier: Modifier = Modifier,
    preview: ImageBitmap? = null
) {
    val di = localDI()
    val images = remember(di) { di.direct.instanceOrNull<AvatarImages>() }
    val image by produceState(preview ?: photoUrl?.let { images?.cached(it) }, photoUrl, preview, images) {
        value = preview ?: photoUrl?.takeIf { it.isNotBlank() }?.let { images?.load(it) }
    }
    Box(
        modifier = modifier
            .size(size)
            .clip(CircleShape)
            .background(MaterialTheme.colorScheme.primaryContainer),
        contentAlignment = Alignment.Center
    ) {
        // SH-158: the photo fades in over the initials once it has loaded (a cached one shows at once).
        Crossfade(image, Modifier.fillMaxSize(), animationSpec = motionTween(), label = "avatar") { bitmap ->
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                if (bitmap != null) {
                    Image(bitmap, contentDescription = null, contentScale = ContentScale.Crop, modifier = Modifier.fillMaxSize())
                } else {
                    Text(
                        initialsOf(name),
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = (size.value * 0.36f).sp
                    )
                }
            }
        }
    }
}

/** The default size of the profile screen's avatar. */
val ProfileAvatarSize = 100.dp
